// ステータス変更確認ダイアログ
// 最後の企業管理者のステータス変更時に警告を表示

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';

interface StatusChangeConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  employeeName: string;
  isLastAdmin: boolean;
  isDeactivating: boolean;
  isLoading: boolean;
}

export const StatusChangeConfirmDialog: React.FC<
  StatusChangeConfirmDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  employeeName,
  isLastAdmin,
  isDeactivating,
  isLoading,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="status-change-dialog-title"
      aria-describedby="status-change-dialog-description"
      role={isLastAdmin && isDeactivating ? 'alertdialog' : 'dialog'}
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ToggleOffIcon
              sx={{ fontSize: 28, color: 'primary.main', mr: 1 }}
            />
            <Typography
              variant="h6"
              component="h2"
              id="status-change-dialog-title"
              sx={{ fontWeight: 600 }}
            >
              ステータス変更の確認
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isLoading}
            aria-label="ダイアログを閉じる"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* 対象従業員情報 */}
        <Box id="status-change-dialog-description" sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>対象:</strong> {employeeName}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>変更内容:</strong>{' '}
            {isDeactivating ? 'アカウントを無効化' : 'アカウントを有効化'}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 最後の企業管理者の場合の警告 */}
        {isLastAdmin && isDeactivating && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon sx={{ color: 'error.main', mr: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                ⚠️ 最後の企業管理者です
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Typography
                component="li"
                variant="body2"
                sx={{ mb: 0.5, color: 'error.main', fontWeight: 600 }}
              >
                あなたは企業内で最後の有効な企業管理者です
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                アカウントを無効化すると、従業員管理機能にアクセスできなくなります
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                再度有効化するには、システム管理者に連絡する必要があります
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: 'error.main', fontWeight: 600 }}
              >
                本当に無効化してよろしいですか？
              </Typography>
            </Box>
          </Alert>
        )}

        {/* 通常の注意事項 */}
        {(!isLastAdmin || !isDeactivating) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ✅ 注意事項
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                この変更は即座に反映されます
              </Typography>
              {isDeactivating ? (
                <>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    無効化されたアカウントはログインできなくなります
                  </Typography>
                  <Typography component="li" variant="body2">
                    後で再度有効化することができます
                  </Typography>
                </>
              ) : (
                <>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    有効化されたアカウントは再度ログインできるようになります
                  </Typography>
                  <Typography component="li" variant="body2">
                    以前の権限が復元されます
                  </Typography>
                </>
              )}
            </Box>
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          disabled={isLoading}
          sx={{ minWidth: 100 }}
        >
          キャンセル
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={isLastAdmin && isDeactivating ? 'error' : 'primary'}
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ minWidth: 140 }}
        >
          {isLoading
            ? '変更中...'
            : isDeactivating
              ? 'アカウントを無効化'
              : 'アカウントを有効化'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
